import Task from '../types/Task';
import { TaskStatus, TaskStatusGroup } from '../types/TaskStatus';
import { WorkspaceEstimationType } from '../types/Workspace';
import { TaskEstimateMapping } from '../components/TaskTable/helpers/helper';

export interface SprintMetrics {
  completedTasksCount: number;
  totalStoryPoints: number;
  completedStoryPoints: number;
  taskProgressTooltip: string;
  storyPointsTooltip: string;
}

/**
 * Generates a tooltip for task progress based on estimation type
 * For story points: Simple count
 * For t-shirt sizes: Breakdown by size (XS, S, M, L, XL)
 */
const generateTaskProgressTooltip = (
  dataSource: Task[],
  completedTaskIds: Set<number>,
  estimationType?: WorkspaceEstimationType
): string => {
  if (estimationType === WorkspaceEstimationType.storyPoints) {
    return `${completedTaskIds.size} out of ${dataSource.length} Tasks done`;
  }

  // For t-shirt sizes, show delivery breakdown
  const estimateMapping = TaskEstimateMapping(WorkspaceEstimationType.tshirtSizes);
  const sizeOrder = ['XS', 'S', 'M', 'L', 'XL']; // Explicit ordering

  const sizeBreakdown: Record<string, { completed: number; total: number }> = {};

  dataSource.forEach((task) => {
    if (task.storyPoints && estimateMapping[task.storyPoints]) {
      const sizeName = estimateMapping[task.storyPoints];
      if (!sizeBreakdown[sizeName]) {
        sizeBreakdown[sizeName] = { completed: 0, total: 0 };
      }
      sizeBreakdown[sizeName].total++;

      // O(1) lookup instead of Array.some()
      if (completedTaskIds.has(task.id)) {
        sizeBreakdown[sizeName].completed++;
      }
    }
  });

  // Sort by size order and only show sizes that exist
  const lines = sizeOrder
    .filter((size) => sizeBreakdown[size] && sizeBreakdown[size].completed > 0 && sizeBreakdown[size].total > 0)
    .map((size) => `${size}: ${sizeBreakdown[size].completed}/${sizeBreakdown[size].total}`)
    .join('\n');

  return lines ? `Delivery breakdown:\n${lines}` : `${completedTaskIds.size} out of ${dataSource.length} Tasks done`;
};

/**
 * Calculates all sprint metrics in a single pass
 * Optimized to O(n) complexity with Set-based lookups
 *
 * @param dataSource - Array of tasks in the sprint
 * @param statusOptions - Available task statuses to check completion
 * @param estimationType - Workspace estimation type (storyPoints or tshirtSizes)
 * @returns Complete sprint metrics including tooltip content
 */
export const calculateSprintMetrics = (
  dataSource: Task[],
  statusOptions: TaskStatus[],
  estimationType?: WorkspaceEstimationType
): SprintMetrics => {
  // Single pass to identify completed tasks with O(1) lookup
  const completedTaskIds = new Set<number>();
  const completedTasks: Task[] = [];

  dataSource.forEach((task) => {
    const taskStatus = statusOptions.find((status) => status.id === task.statusId);
    if (taskStatus?.group === TaskStatusGroup.completed) {
      completedTaskIds.add(task.id);
      completedTasks.push(task);
    }
  });

  // Calculate story points (single pass)
  let totalStoryPoints = 0;
  let completedStoryPoints = 0;

  dataSource.forEach((task) => {
    if (task.storyPoints && estimationType === WorkspaceEstimationType.storyPoints) {
      const storyPointValue = Number(TaskEstimateMapping(WorkspaceEstimationType.storyPoints)[task.storyPoints]);
      totalStoryPoints += storyPointValue;

      if (completedTaskIds.has(task.id)) {
        completedStoryPoints += storyPointValue;
      }
    }
  });

  // Generate tooltips
  const taskProgressTooltip = generateTaskProgressTooltip(dataSource, completedTaskIds, estimationType);

  const storyPointsTooltip = `${completedStoryPoints} out of ${totalStoryPoints} Story Points done`;

  return {
    completedTasksCount: completedTaskIds.size,
    totalStoryPoints,
    completedStoryPoints,
    taskProgressTooltip,
    storyPointsTooltip,
  };
};
